import React from 'react';
import { Popover, Box, Stack, Flex } from '@mantine/core';
import { HoverDisplay } from '@/components/HoverDisplay';

export interface ListItemBase {
  value?: string | undefined;
  label?: string;
  primary?: boolean;
  [key: string]: any;
}

interface ListInputProps<T extends ListItemBase> {
  items: T[];
  renderItem: (item: T, idx: number) => React.ReactNode;
  addForm: React.ReactNode;
  popoverOpened?: boolean;
  setPopoverOpened?: (opened: boolean) => void;
  renderItemActions?: (item: T, idx: number) => React.ReactNode;
  alwaysShowActions?: boolean;
  itemClassName?: string;
}

export function ListInput<T extends ListItemBase>({
  items,
  renderItem,
  addForm,
  popoverOpened,
  setPopoverOpened,
  renderItemActions,
  alwaysShowActions = false,
  itemClassName,
}: ListInputProps<T>) {
  const [internalOpened, setInternalOpened] = React.useState(false);
  const opened = popoverOpened !== undefined ? popoverOpened : internalOpened;
  const setOpened = setPopoverOpened ?? setInternalOpened;

  return (
    <Box>
      <Stack gap={2}>
        {items.map((item, idx) => (
          <HoverDisplay key={idx}>
            <Flex justify="space-between" align="center" className={itemClassName} w="100%" py={4}>
              {/* `flex: 1, minWidth: 0` lets the item content shrink below its
                  intrinsic size so long text truncates instead of pushing the
                  actions slot off-screen. Removed the redundant `width: 100%`
                  which would otherwise claim the whole row regardless of flex
                  arithmetic. */}
              <Box style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                {renderItem(item, idx)}
              </Box>
              {/* `flexShrink: 0` reserves the actions slot — keeps the 3-dot
                  menu reachable even when the primary content is very long. */}
              {renderItemActions &&
                (alwaysShowActions ? (
                  <Box ml={8} style={{ flexShrink: 0 }}>
                    {renderItemActions(item, idx)}
                  </Box>
                ) : (
                  <HoverDisplay.Target>
                    <Box ml={8} style={{ flexShrink: 0 }}>
                      {renderItemActions(item, idx)}
                    </Box>
                  </HoverDisplay.Target>
                ))}
            </Flex>
          </HoverDisplay>
        ))}
      </Stack>
      <Popover opened={opened} onChange={setOpened} position="bottom-start" offset={4} withArrow shadow="md">
        <Popover.Target>
          {/* Empty target, button is rendered externally */}
          <span />
        </Popover.Target>
        <Popover.Dropdown>{addForm}</Popover.Dropdown>
      </Popover>
    </Box>
  );
}
